import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { BsPlus, BsDash } from 'react-icons/bs';
import StyledMobileMainMenuCardUniversal from './MobileMainMenuCardUniversal.styled';
import { buildNavLink } from '../../helpers/nav-link';

const DEFAULT_CLASS_NAMES = { heading: 't-body-medium', item: 't-list-copy' };
const DEFAULT_ICONS = { plus: (p) => <BsPlus {...p} />, minus: (p) => <BsDash {...p} /> };
const DEFAULT_TRAILING_LINK = { to: '/shop', text: 'SHOP ALL', brandTo: '/brands', brandText: 'ALL BRANDS' };

/**
 * One accordion card for every marketplace. The markup is the rebud one
 * (MobileMainMenuCard); the heading/item typography classes, the plus/minus
 * icons, the trailing link and the three brand-specific CSS values come from
 * headerConfig.mobileMenuConfig.card of the selected market config.
 */
const MobileMainMenuCardUniversal = React.memo((props) => {
  const [isOpen, setIsOpen] = useState();
  const [openSubs, setOpenSubs] = useState({});
  const toggleElement = (element) => {
    const el = element.currentTarget.nextElementSibling;
    setIsOpen(!isOpen);
    if (!isOpen) {
      el.style.height = el.scrollHeight + 'px';
    } else {
      // height may be 'auto' after a subcategory toggle; pin it so the collapse animates
      el.style.height = el.scrollHeight + 'px';
      el.getBoundingClientRect();
      el.style.height = '0px';
    }
  };

  const toggleSub = (e, i) => {
    e.currentTarget.closest('.collapse__body').style.height = 'auto';
    setOpenSubs((prev) => ({ ...prev, [i]: !prev[i] }));
  };

  const {
    heading,
    list = [],
    type,
    toggleMenu,
    classNames = DEFAULT_CLASS_NAMES,
    icons = DEFAULT_ICONS,
    trailingLink = DEFAULT_TRAILING_LINK,
    styling = {},
  } = props;

  const headingClass = classNames.heading || DEFAULT_CLASS_NAMES.heading;
  const itemClass = classNames.item || DEFAULT_CLASS_NAMES.item;
  const Plus = icons.plus || DEFAULT_ICONS.plus;
  const Minus = icons.minus || DEFAULT_ICONS.minus;

  const isBrand = type === 'brand';
  const trailingTo = (isBrand && trailingLink?.brandTo) || trailingLink?.to;
  const trailingText = (isBrand && trailingLink?.brandText) || trailingLink?.text;

  return (
    <StyledMobileMainMenuCardUniversal themeProps={styling}>
      <div className="collapse__element">
        <div data-testid="collapse__header" onClick={(e) => toggleElement(e)} className="collapse__header">
          <h2 className={headingClass}>{heading}</h2>
          <span className="plus-minus_icon">{isOpen ? <Minus /> : <Plus />}</span>
        </div>
        <div className={`collapse__body ${!isOpen ? 'hidden' : ''}`}>
          {list.map((item, i) =>
            type === 'category' && item.children?.length > 0 ? (
              <div key={i}>
                <div className="collapse__row">
                  <Link
                    onClick={toggleMenu}
                    className={`collapse__item ${itemClass}`}
                    to={buildNavLink(type, item.uri_param)}
                  >
                    {item.name}
                  </Link>
                  <button
                    type="button"
                    className="collapse__toggle"
                    onClick={(e) => toggleSub(e, i)}
                    aria-expanded={!!openSubs[i]}
                    aria-label={`Toggle ${item.name} subcategories`}
                  >
                    {openSubs[i] ? <Minus /> : <Plus />}
                  </button>
                </div>
                <div className={`collapse__sublist ${openSubs[i] ? 'collapse__sublist--open' : ''}`}>
                  {item.children.map((sub) => (
                    <Link
                      onClick={toggleMenu}
                      className={`collapse__item ${itemClass}`}
                      key={sub.uri_param}
                      to={buildNavLink(type, `${item.uri_param}/${sub.uri_param}`)}
                    >
                      {sub.name}
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <Link
                onClick={toggleMenu}
                className={`collapse__item ${itemClass}`}
                key={i}
                to={buildNavLink(type, item.uri_param)}
              >
                {item.name}
              </Link>
            )
          )}
          {trailingLink && (
            <Link onClick={toggleMenu} className={`collapse__item ${itemClass}`} to={trailingTo}>
              {trailingText}
            </Link>
          )}
        </div>
      </div>
    </StyledMobileMainMenuCardUniversal>
  );
});

MobileMainMenuCardUniversal.propTypes = {
  heading: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  list: PropTypes.array,
  toggleMenu: PropTypes.func,
  classNames: PropTypes.shape({
    heading: PropTypes.string,
    item: PropTypes.string,
  }),
  icons: PropTypes.shape({
    plus: PropTypes.func,
    minus: PropTypes.func,
  }),
  trailingLink: PropTypes.shape({
    to: PropTypes.string,
    text: PropTypes.string,
    brandTo: PropTypes.string,
    brandText: PropTypes.string,
  }),
  styling: PropTypes.object,
};

export default MobileMainMenuCardUniversal;
